// src/agent-activity.ts
var record = (value) => value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};
var list = (value) => Array.isArray(value) ? value : [];
var ids = (value) => [...new Set(list(value).filter((id) => typeof id === "string" && id.length > 0))];
var point = (value) => typeof value.x === "number" && Number.isFinite(value.x) && typeof value.y === "number" && Number.isFinite(value.y) ? { x: value.x, y: value.y } : void 0;
var TOOL_LABELS = {
  inspect_canvas: "Reading canvas",
  apply_canvas_changes: "Editing canvas",
  generate_images: "Generating images",
  generate_3d: "Building 3D",
  inspect_jobs: "Checking outputs",
  view_images: "Reviewing images",
  search_internet: "Finding references",
  collect_references: "Adding references",
  view_references: "Reviewing references",
  get_recipe: "Planning",
  list_tools: "Choosing tools",
  inspect_comfy: "Checking local tools",
  run_comfy_workflow: "Running workflow",
  collect_comfy_output: "Adding output",
  ask_choices: "Preparing questions"
};
var EDIT_LABELS = {
  upscale: "Upscaling",
  "remove-background": "Removing background",
  crop: "Cropping",
  inpaint: "Inpainting",
  erase: "Erasing",
  expand: "Expanding",
  variations: "Making variations",
  vectorize: "Vectorizing",
  animate: "Animating",
  blur: "Blurring",
  sharpen: "Sharpening",
  recolor: "Recoloring",
  reframe: "Reframing"
};
function toolLabel(tool) {
  if (tool.name === "run_tool") {
    const id = String(tool.input.tool).replace(/^genie\.studio\./, "");
    return Object.hasOwn(EDIT_LABELS, id) ? EDIT_LABELS[id] : "Editing frame";
  }
  return Object.hasOwn(TOOL_LABELS, tool.name) ? TOOL_LABELS[tool.name] : "Working";
}
function toolFocus(tool, run) {
  const input = tool.input;
  switch (tool.name) {
    case "run_tool":
      return { frameIds: ids([input.frameId]) };
    case "inspect_canvas":
    case "view_images":
      return { frameIds: ids(input.artboardIds) };
    case "generate_3d":
      return { frameIds: input.sourceArtboardId ? ids([input.sourceArtboardId]) : [...run.selectedIds] };
    case "generate_images":
      return { frameIds: ids(list(input.images).map((image) => record(image).sourceArtboardId)) };
    case "inspect_jobs":
      return { frameIds: ids(ids(input.jobIds).map((id) => run.jobs.get(id))) };
    case "apply_canvas_changes": {
      const changes = list(input.changes).map(record);
      return { frameIds: ids(changes.map((change) => change.artboardId)), point: changes.map(point).find(Boolean) };
    }
    case "collect_references":
    case "collect_comfy_output":
      return { frameIds: [], point: point(input) };
    default:
      return { frameIds: [] };
  }
}
function resultFocus(tool, result, run) {
  const value = record(result);
  const jobs = [...list(value.jobs), ...tool.name === "run_comfy_workflow" ? [value] : []].map(record);
  for (const job of jobs) if (typeof job.id === "string" && typeof job.artboardId === "string") run.jobs.set(job.id, job.artboardId);
  const generated = ids(jobs.map((job) => job.artboardId));
  if (generated.length) return { frameIds: generated };
  if (tool.name === "run_tool") {
    const output = ids(value.outputIds);
    if (output.length) return { frameIds: output };
  }
  if (value.committed === true && value.canvasId === run.canvasId) {
    return { frameIds: ids(list(value.objects).map(record).filter((object) => !object.deleted).map((object) => object.id)) };
  }
  return void 0;
}
function createAgentActivityStore() {
  const runs = /* @__PURE__ */ new Map();
  const snapshots = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Map();
  const publish = (canvasId) => {
    const active = [...runs.entries()].reverse().find(([, run]) => run.canvasId === canvasId && !run.waiting);
    let next;
    if (active) {
      const [runId, run] = active;
      const tool = [...run.tools.values()].at(-1);
      next = { runId, label: tool ? toolLabel(tool) : run.label, ...tool ? toolFocus(tool, run) : run.focus };
    }
    const prev = snapshots.get(canvasId);
    if (JSON.stringify(prev) === JSON.stringify(next)) return;
    if (next) snapshots.set(canvasId, next);
    else snapshots.delete(canvasId);
    listeners.get(canvasId)?.forEach((listener) => listener());
  };
  const end = (runId) => {
    const run = runs.get(runId);
    if (!run) return;
    runs.delete(runId);
    publish(run.canvasId);
  };
  return {
    get: (canvasId) => snapshots.get(canvasId) ?? null,
    subscribe(canvasId, listener) {
      const group = listeners.get(canvasId) ?? /* @__PURE__ */ new Set();
      group.add(listener);
      listeners.set(canvasId, group);
      return () => {
        group.delete(listener);
        if (!group.size) listeners.delete(canvasId);
      };
    },
    begin(request) {
      if (runs.has(request.runId)) return;
      runs.set(request.runId, {
        canvasId: request.canvasId,
        selectedIds: [...request.selectedIds],
        focus: { frameIds: [...request.selectedIds] },
        label: "Thinking",
        waiting: false,
        tools: /* @__PURE__ */ new Map(),
        jobs: /* @__PURE__ */ new Map()
      });
      publish(request.canvasId);
    },
    event(runId, event) {
      const run = runs.get(runId);
      if (!run) return;
      if (event.type === "finished") {
        if (event.runId === runId) end(runId);
        return;
      }
      if (event.type === "error") {
        end(runId);
        return;
      }
      if (event.type === "clarify") run.waiting = true;
      if (event.type === "status") run.label = /canvas/i.test(event.message) ? "Reading canvas" : "Thinking";
      if (event.type === "text") run.label = "Replying";
      if (event.type === "tool_start") {
        run.tools.set(event.id, event);
        run.label = "Thinking";
      }
      if (event.type === "tool_result") {
        const tool = run.tools.get(event.id);
        if (tool) {
          run.focus = !event.failed ? resultFocus(tool, event.result, run) ?? toolFocus(tool, run) : { frameIds: [] };
          run.tools.delete(event.id);
          run.label = "Thinking";
        }
      }
      publish(run.canvasId);
    },
    end
  };
}
var agentActivity = createAgentActivityStore();
function placeAgentCursor(rects, target, viewport) {
  const { width, height, transform: [tx, ty, zoom] } = viewport;
  const projected = rects.map((rect) => ({ x: rect.x * zoom + tx, y: rect.y * zoom + ty, width: rect.width * zoom, height: rect.height * zoom }));
  const visible = projected.find((rect) => rect.x < width && rect.y < height && rect.x + rect.width > 0 && rect.y + rect.height > 0);
  const location = visible ? { x: visible.x + visible.width - 16, y: visible.y + 16 } : target ? { x: target.x * zoom + tx, y: target.y * zoom + ty } : void 0;
  const offscreen = !visible && (projected.length > 0 || Boolean(location && (location.x < 0 || location.x > width || location.y < 0 || location.y > height)));
  if (!location || offscreen) return { x: Math.max(12, width - 24), y: 24, docked: true, offscreen, flip: true };
  const x = Math.max(12, Math.min(location.x, width - 28));
  const y = Math.max(12, Math.min(location.y, height - 78));
  return { x, y, docked: false, offscreen: false, flip: x + 230 > width };
}

export {
  createAgentActivityStore,
  agentActivity,
  placeAgentCursor
};
//# sourceMappingURL=chunk-P5UNJCZG.js.map